import type { Node } from '../../../core/models/scene';
import type { NodeChangeRecord } from '../../../core/commands/base/BaseNodeCommand';
import { globalEventBus } from '../../../core/events/IEventBus';
import { DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT } from '../constants/mediaReleaseEvents';
import { extractStoredMediaObjectKeysFromNode } from './extractStoredMediaObjectKeysFromNode';
import { enqueueDiagramMediaReferenceDelta } from './mediaReferenceDeltaQueue';

type MediaReferenceDelta = {
  addedKeys?: string[];
  removedKeys?: string[];
};

function normalizeKeyList(keys: Iterable<string>): string[] {
  const out = new Set<string>();
  for (const raw of keys) {
    if (typeof raw !== 'string') continue;
    const key = raw.trim();
    if (!key) continue;
    out.add(key);
  }
  return Array.from(out);
}

function getWindowDiagramId(): string {
  if (typeof window === 'undefined') return '';
  return ((window as unknown as { __DIAGRAM_ID__?: string }).__DIAGRAM_ID__ ?? '').trim();
}

export function collectMediaKeysFromNodes(nodes: Array<Node | Partial<Node> | null | undefined>): string[] {
  const all: string[] = [];
  for (const node of nodes) {
    if (!node) continue;
    all.push(...extractStoredMediaObjectKeysFromNode(node as Node));
  }
  return normalizeKeyList(all);
}

export function emitDiagramMediaReferenceDelta(delta: MediaReferenceDelta): void {
  const diagramId = getWindowDiagramId();
  if (!diagramId) return;
  const addedKeys = normalizeKeyList(delta.addedKeys ?? []);
  const removedKeys = normalizeKeyList(delta.removedKeys ?? []);
  if (addedKeys.length === 0 && removedKeys.length === 0) return;
  enqueueDiagramMediaReferenceDelta(diagramId, { addedKeys, removedKeys });
  globalEventBus.emit(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, {
    diagramId,
    ...(addedKeys.length > 0 ? { addedKeys } : {}),
    ...(removedKeys.length > 0 ? { removedKeys } : {}),
  });
}

export function calculateMediaDeltaFromChangeRecords(records: NodeChangeRecord[]): {
  addedKeys: string[];
  removedKeys: string[];
} {
  const added = new Set<string>();
  const removed = new Set<string>();

  for (const record of records) {
    const oldKeys = new Set(collectMediaKeysFromNodes([record.oldState]));
    const newKeys = new Set(collectMediaKeysFromNodes([record.newState]));

    for (const key of newKeys) {
      if (!oldKeys.has(key)) {
        added.add(key);
      }
    }
    for (const key of oldKeys) {
      if (!newKeys.has(key)) {
        removed.add(key);
      }
    }
  }

  return {
    addedKeys: Array.from(added),
    removedKeys: Array.from(removed),
  };
}
